'use es6';

import PropTypes from 'prop-types';
import { useEffect, useState, useCallback, useReducer, useRef } from 'react';
import styled from 'styled-components';
import EmbedIframe from './EmbedIframe';
import { getContentEmbedUIOrigin, ContentEmbedUIEvents, logDebug } from '../../constants/embed';
import { jsx as _jsx } from "react/jsx-runtime";
const MAX_PREVIEW_HEIGHT_DEFAULT = 300;
const IFRAME_PREVIEW_ID = 'embed-modal-preview';
const initialStyles = {
  iframeStyle: {
    width: '100%',
    height: `${MAX_PREVIEW_HEIGHT_DEFAULT}px`,
    border: 'none'
  },
  containerStyle: {
    overflow: 'hidden',
    display: 'none'
  }
};
function styleReducer(state, action) {
  switch (action.type) {
    case 'UPDATE_IFRAME_STYLE':
      return {
        iframeStyle: Object.assign({}, state.iframeStyle, action.payload),
        containerStyle: Object.assign({}, state.containerStyle)
      };
    case 'UPDATE_CONTAINER_STYLE':
      return {
        iframeStyle: Object.assign({}, state.iframeStyle),
        containerStyle: Object.assign({}, state.containerStyle, action.payload)
      };
    default:
      return state;
  }
}
const EmbedIframeContainer = styled.div.withConfig({
  displayName: "EmbedLivePreview__EmbedIframeContainer",
  componentId: "sc-1or2swm-0"
})(["max-height:", "px;"], props => props.maxHeight);
const ContentEmbedUIOrigin = getContentEmbedUIOrigin();
function EmbedLivePreview(props) {
  const {
    data: {
      html
    },
    onSetContentDone,
    showPreview = false,
    maxHeight = MAX_PREVIEW_HEIGHT_DEFAULT
  } = props;
  const [targetWindow, setTargetWindow] = useState(null);
  const [styles, dispatchStyles] = useReducer(styleReducer, initialStyles);
  const contentRef = useRef(html);
  const postMessageToIframe = useCallback(msg => {
    if (!targetWindow) return;
    logDebug('Sending postMessage to content-embed-ui iframe:', msg, ContentEmbedUIOrigin);
    targetWindow.postMessage(Object.assign({}, msg, {
      id: IFRAME_PREVIEW_ID
    }), ContentEmbedUIOrigin);
  }, [targetWindow]);
  useEffect(() => {
    const payload = {
      display: showPreview ? 'flex' : 'none'
    };
    if (!showPreview) {
      // Reset the scroll overflow
      payload.overflow = 'hidden';
    }
    dispatchStyles({
      type: 'UPDATE_CONTAINER_STYLE',
      payload
    });
  }, [showPreview]);
  useEffect(() => {
    if (!html) return;
    contentRef.current = html;

    // Asynchronously set the iframe content if `html` prop changed after
    // the connection with the Iframe was already made
    postMessageToIframe({
      action: ContentEmbedUIEvents.SET_CONTENT,
      payload: {
        html
      }
    });
  }, [postMessageToIframe, html]);
  useEffect(() => {
    const onPostMessage = evt => {
      const {
        origin,
        source,
        data
      } = evt;
      if (!targetWindow || origin !== ContentEmbedUIOrigin || source !== targetWindow) {
        return;
      }

      // Interframe messages are encapsulated in the { body } property
      const {
        action,
        id,
        payload
      } = data.body || {};
      if (id !== IFRAME_PREVIEW_ID) return;
      logDebug('Received postMessage from content-embed-ui iframe:', data, origin);
      if (action === ContentEmbedUIEvents.READY && contentRef.current) {
        postMessageToIframe({
          action: ContentEmbedUIEvents.SET_CONTENT,
          payload: {
            html: contentRef.current
          }
        });
      } else if (action === ContentEmbedUIEvents.SET_CONTENT_DONE) {
        onSetContentDone();
        dispatchStyles({
          type: 'UPDATE_CONTAINER_STYLE',
          payload: {
            display: 'flex'
          }
        });
      } else if (action === ContentEmbedUIEvents.SIZE_CHANGE) {
        const {
          height: newHeight
        } = payload;
        if (newHeight !== 0) {
          dispatchStyles({
            type: 'UPDATE_IFRAME_STYLE',
            payload: {
              height: `${newHeight}px`
            }
          });
          dispatchStyles({
            type: 'UPDATE_CONTAINER_STYLE',
            payload: {
              overflow: newHeight > maxHeight ? 'auto' : 'hidden'
            }
          });
        }
      }
    };
    window.addEventListener('message', onPostMessage);
    return () => {
      // Cleanup the content-embed-ui app
      postMessageToIframe({
        action: ContentEmbedUIEvents.DISCONNECT
      });
      window.removeEventListener('message', onPostMessage);
    };
  }, [targetWindow, maxHeight, postMessageToIframe, onSetContentDone]);
  const {
    containerStyle,
    iframeStyle
  } = styles;
  return (
    /*#__PURE__*/
    // @bend-expect trellis-tokenized
    // @bend-expect trellis-tokenized
    _jsx(EmbedIframeContainer, {
      style: containerStyle,
      maxHeight: maxHeight,
      children: /*#__PURE__*/_jsx(EmbedIframe, {
        inpage: false,
        embedId: IFRAME_PREVIEW_ID,
        style: iframeStyle,
        iframeRef: el => {
          if (el && el.contentWindow) {
            setTargetWindow(el.contentWindow);
          }
        }
      })
    })
  );
}
export default EmbedLivePreview;
EmbedLivePreview.propTypes = {
  data: PropTypes.object.isRequired,
  onSetContentDone: PropTypes.func.isRequired,
  showPreview: PropTypes.bool,
  maxHeight: PropTypes.number
};